import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { authFetch } from '../services/authFetch';
import { API_BASE_URL } from '../apiClient';

// ── Shapes returned by GET /api/apmt/servicenow-availability ─────────────────

interface Cell {
  pct: number | null;
  downtime_seconds: number;
  eligible_seconds: number;
  incident_count: number;
}

interface Period {
  key: string;
  label: string;
  start: string;
  end: string;
}

// P3 MI: informational distinct incident counts only (no status colour, no target).
interface MiBlock {
  periods: Record<string, { incident_count: number }>;
  total: { incident_count: number };
}

interface Terminal {
  name: string;
  periods: Record<string, Cell>;
  total: Cell;
  p3_mi?: MiBlock;
}

interface Region {
  name: string;
  export_name: string;
  terminal_count: number;
  periods: Record<string, Cell>;
  total: Cell;
  terminals: Terminal[];
  p3_mi?: MiBlock;
}

interface WeeklyData {
  period_type: string;
  time_range: string;
  latest_valid_timestamp: string;
  latest_week: string;
  periods: Period[];
  availability_target_pct: number;
  global_p1p2_target: number | null;
  global_p1p2_targets: Record<'weekly' | 'monthly' | 'quarterly' | 'yearly', number | null>;
  global_p1p2: {
    periods: Record<string, { incident_count: number }>;
    total: { incident_count: number };
  };
  global_p3_mi?: MiBlock;
  met_target: {
    periods: Record<string, { met: number; total: number }>;
    total: { met: number; total: number };
  };
  global: { periods: Record<string, Cell>; total: Cell };
  regions: Region[];
  unmapped_terminals: string[];
  validation: {
    source_rows: number;
    accepted_rows: number;
    rejected: { source_row: number; reason: string; terminal?: string; incident?: string }[];
    zero_duration_rows: number;
    distinct_outages: number;
    distinct_incidents: number;
    distinct_terminals: number;
    abnormal_overflow: unknown[];
  };
}

// ── Formatting and colour rules ──────────────────────────────────────────────

const GREEN = '#4caf50';
const RED = '#f44336';

// Two decimals, standard rounding, never truncated. The underlying value is never rounded before this.
export const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  return `${value.toFixed(2)}%`;
};

// '% Met Target' cell text: "X out of Y" with the share underneath, rounded to a whole number.
// Availability-only: met counts terminals at or above the target. No eligible terminals means no percentage.
export const metTargetText = (met: number, total: number): ReactNode => {
  if (!total) return 'No eligible terminals';
  const pct = Math.round((met / total) * 100);
  return (
    <>
      {`${met} out of ${total}`}
      <br />
      {`(${pct}%)`}
    </>
  );
};

// Green when the unrounded Actual is at or above the target, red when below. Blank means no colour.
const availabilityColor = (pct: number | null | undefined, target: number) => {
  if (pct === null || pct === undefined) return '#ffffff';
  return pct >= target ? GREEN : RED;
};

// Footnote wording for each period type's P1/P2 target.
const PERIOD_NOUN: Record<'weekly' | 'monthly' | 'quarterly' | 'yearly', string> = {
  weekly: 'week',
  monthly: 'month',
  quarterly: 'quarter',
  yearly: 'year',
};

// P1/P2 cells: green at or under the period's target, red above it. With no target defined
// (quarterly, yearly) the count stays neutral rather than being coloured against a made-up threshold.
const incidentColor = (count: number, maximum: number | null) => {
  if (maximum === null || maximum === undefined) return '#ffffff';
  return count > maximum ? RED : GREEN;
};

// ── Styles ───────────────────────────────────────────────────────────────────

const HDR_STYLE = {
  backgroundColor: '#e3f2fd',
  fontWeight: 700,
  fontSize: '0.8rem',
  padding: '8px 10px',
  textAlign: 'center' as const,
  whiteSpace: 'nowrap' as const,
};

const LABEL_CELL = { fontWeight: 600, fontSize: '0.8rem', padding: '6px 10px', whiteSpace: 'nowrap' as const };

const VALUE_CELL = {
  fontSize: '0.8rem',
  textAlign: 'center' as const,
  padding: '6px 8px',
  minWidth: 84,
  borderRight: '1px solid #e0e0e0',
};

const TOTAL_CELL = { ...VALUE_CELL, backgroundColor: '#f5f5f5', fontWeight: 700 };

const NAME_CELL = {
  fontWeight: 700,
  fontSize: '0.85rem',
  padding: '6px 10px',
  whiteSpace: 'nowrap' as const,
  borderRight: '1px solid #e0e0e0',
  verticalAlign: 'middle' as const,
  position: 'sticky' as const,
  left: 0,
  zIndex: 1,
  backgroundColor: '#ffffff',
};

const TERMINAL_NAME_CELL = {
  ...NAME_CELL,
  fontWeight: 400,
  fontSize: '0.8rem',
  padding: '6px 10px 6px 36px',
  color: '#37474f',
};

const TARGET_LABEL = { ...LABEL_CELL, color: '#1565c0' };
const P12_LABEL = { ...LABEL_CELL, color: '#6a1b9a' };

// ── Helpers ──────────────────────────────────────────────────────────────────

const csvEscape = (v: string | number | null | undefined) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const isoYearWeek = (key: string) => {
  const m = key.match(/^(\d{4})-W(\d{2})$/);
  return m ? { year: m[1], week: m[2] } : null;
};

export interface AvailabilitySource {
  endpoint: string;            // backend path, e.g. /api/apmt/servicenow-availability
  title: string;
  showPreviousYear?: boolean;  // adds a 'Previous Year' time range
}

// What a user sees when the data cannot be loaded. Details stay in the backend logs and diagnostics.
const UNAVAILABLE_MESSAGE = 'Availability data is temporarily unavailable. Please try again later.';

// An error whose message is safe to show to a user. Any other error is shown as UNAVAILABLE_MESSAGE.
const userFacingError = (message: string) => Object.assign(new Error(message), { userFacing: true });

export default function TerminalsAvailability({ source }: { source: AvailabilitySource }) {
  const [data, setData] = useState<WeeklyData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [warmingUp, setWarmingUp] = useState(false);   // true while the server is still doing its first pull
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Filters
  const [periodType, setPeriodType] = useState<'weekly' | 'monthly' | 'quarterly' | 'yearly'>('weekly');
  const [timeRange, setTimeRange] = useState<'ytd' | 'prev_year' | 'all' | 'last7' | 'last30' | 'last90' | 'custom'>('ytd');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [terminalFilter, setTerminalFilter] = useState<string>('all');
  // EAI restricts only P1/P2 counts (Global/Region/Terminal/Total); Availability and % Met Target are unaffected server-side.
  const [eaiFilter, setEaiFilter] = useState<'no' | 'yes'>('no');

  const customInvalid = timeRange === 'custom' && (!customStart || !customEnd || customStart > customEnd);

  // Loads the availability data for the current period type, time range, custom dates and EAI selection.
  const loadData = async (signal?: AbortSignal) => {
    if (customInvalid) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ period_type: periodType, time_range: timeRange, eai: eaiFilter });
      if (timeRange === 'custom') {
        params.set('start', customStart);
        params.set('end', customEnd);
      }
      const res = await authFetch(`${API_BASE_URL}${source.endpoint}?${params.toString()}`, { signal });
      const body = await res.json();
      if (res.status === 202 && body.loading) {
        setWarmingUp(true);          // the server is still doing its first pull; try again shortly
        return;
      }
      setWarmingUp(false);
      if (!res.ok || !body.success) {
        if (res.status === 401 || res.status === 403) throw userFacingError('You do not have access to Availability data.');
        if (body.error_kind === 'no_source_data') throw userFacingError('No Availability data is available for this reporting period.');
        throw userFacingError(UNAVAILABLE_MESSAGE);
      }
      // An out-of-date backend returns an older shape. Show the generic message instead of crashing the page.
      if (!body.data?.global_p1p2?.periods || !body.data?.global_p1p2_targets) throw userFacingError(UNAVAILABLE_MESSAGE);
      setData(body.data as WeeklyData);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(err instanceof Error && (err as { userFacing?: boolean }).userFacing ? err.message : UNAVAILABLE_MESSAGE);
    } finally {
      // A cancelled request (StrictMode double-invoke, rapid filter changes) must not clear the loading
      // flag the still in-flight, superseding request owns — that would blank the page while data is null.
      if (!signal?.aborted) setLoading(false);
    }
  };

  // While the server warms its cache, ask again every few seconds instead of showing an error or zeros.
  useEffect(() => {
    if (!warmingUp) return undefined;
    const timer = window.setInterval(() => loadData(), 5000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warmingUp, periodType, timeRange, customStart, customEnd, eaiFilter]);

  // Reload when a filter that changes the data changes. An earlier request is cancelled so a slow
  // response can't overwrite a newer one.
  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
    // loadData reads only the filter values listed here, so they are the complete dependency set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType, timeRange, customStart, customEnd, eaiFilter]);

  // Changing Region clears the Terminal if it doesn't belong to the new Region.
  const onRegionChange = (value: string) => {
    setRegionFilter(value);
    if (data && terminalFilter !== 'all' && value !== 'all') {
      const owner = data.regions.find(r => r.terminals.some(t => t.name === terminalFilter));
      if (!owner || owner.name !== value) setTerminalFilter('all');
    }
  };

  const toggleRegion = (name: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const visibleRegions = useMemo(() => {
    if (!data) return [];
    return data.regions.filter(r => regionFilter === 'all' || r.name === regionFilter);
  }, [data, regionFilter]);

  const terminalOptions = useMemo(
    () => visibleRegions.flatMap(r => r.terminals.map(t => t.name)),
    [visibleRegions]
  );

  // Selecting a terminal opens its region so the row is visible.
  useEffect(() => {
    if (!data || terminalFilter === 'all') return;
    const owner = data.regions.find(r => r.terminals.some(t => t.name === terminalFilter));
    if (owner) setExpanded(prev => new Set(prev).add(owner.name));
  }, [terminalFilter, data]);

  if (warmingUp && !data) {
    return (
      <Alert severity="info" icon={<CircularProgress size={18} />}>
        Availability data is loading. This page refreshes itself every few seconds.
      </Alert>
    );
  }
  if (error && !data) return <Alert severity="error">{error}</Alert>;
  if (loading && !data) return <Typography sx={{ p: 2 }}>Loading availability…</Typography>;
  if (!data) return null;

  const periods = data.periods;
  const target = data.availability_target_pct;
  // The P1/P2 target depends on the selected Period Type (weekly 2, monthly 7, quarterly and yearly none).
  // The overall (Global) P1/P2 target for the selected period type. It applies to the whole Terminal estate only.
  const incidentMax = data.global_p1p2_targets?.[periodType] ?? null;
  const targetText = formatPercentage(target);

  // The Global target describes the whole Terminal estate, so hide it once a Region or Terminal is chosen.
  const showGlobalP12 = regionFilter === 'all' && terminalFilter === 'all';
  const regionsToShow = visibleRegions
    .map(r => ({ ...r, terminals: r.terminals.filter(t => terminalFilter === 'all' || t.name === terminalFilter) }))
    .filter(r => terminalFilter === 'all' || r.terminals.length > 0);

  // ── Cells ──

  const actualCell = (cell: Cell | undefined, key: string) => (
    <TableCell
      key={key}
      sx={{
        ...VALUE_CELL,
        backgroundColor: availabilityColor(cell?.pct, target),
        color: cell?.pct === null || cell?.pct === undefined ? 'text.primary' : '#ffffff',
        fontWeight: 700,
      }}
    >
      {formatPercentage(cell?.pct)}
    </TableCell>
  );

  // Region and Terminal P1/P2 counts are informational. The Global target is not a per-Region or per-Terminal
  // target, so these cells are neutral (no green or red).
  // Neutral count cell, shared by the Region/Terminal P1/P2 and P3 MI rows.
  const p12Cell = (cell: { incident_count: number } | undefined, key: string) => (
    <TableCell key={key} sx={{ ...VALUE_CELL, fontWeight: 700 }}>
      {cell?.incident_count ?? 0}
    </TableCell>
  );

  // Global P1/P2 only: green at or under the period's overall target, red above it.
  const globalP12Cell = (count: number, key: string) => (
    <TableCell key={key} sx={{ ...VALUE_CELL, backgroundColor: incidentColor(count, incidentMax), color: '#ffffff', fontWeight: 700 }}>
      {count}
    </TableCell>
  );

  const targetCells = (rowKey: string) =>
    periods.map(p => <TableCell key={`${rowKey}-t-${p.key}`} sx={VALUE_CELL}>{targetText}</TableCell>);

  // Global P1/P2 Target row: the overall target for the selected period type. Its Total cell stays blank,
  // because summing period targets would be a misleading figure.
  const globalTargetCells = () =>
    periods.map(p => (
      <TableCell key={`global-pt-${p.key}`} sx={{ ...VALUE_CELL, color: '#6a1b9a' }}>
        {incidentMax === null ? '' : incidentMax}
      </TableCell>
    ));

  // One Total column. Target shows the target; Actual shows the average Availability; P1/P2 shows the
  // distinct incident total, in neutral styling because it is a period total, not a weekly measurement.
  const targetTotalCell = (rowKey: string) => (
    <TableCell key={`${rowKey}-total-target`} sx={TOTAL_CELL}>{targetText}</TableCell>
  );

  const actualTotalCell = (rowKey: string, total: Cell | undefined) => (
    <TableCell
      key={`${rowKey}-total-actual`}
      sx={{
        ...TOTAL_CELL,
        backgroundColor: availabilityColor(total?.pct, target),
        color: total?.pct === null || total?.pct === undefined ? 'text.primary' : '#ffffff',
      }}
    >
      {formatPercentage(total?.pct)}
    </TableCell>
  );

  const p12TotalCell = (rowKey: string, total: { incident_count: number } | undefined) => (
    <TableCell key={`${rowKey}-total-p12`} sx={{ ...TOTAL_CELL, color: 'text.primary' }}>
      {total?.incident_count ?? 0}
    </TableCell>
  );

  // One location: Target, Actual, P1/P2 and (when the backend sends it) P3 MI rows. The name spans all of them.
  const locationBlock = (
    name: React.ReactNode,
    rowKey: string,
    cells: Record<string, Cell> | undefined,
    total: Cell | undefined,
    rowBg?: string,
    terminal = false,
    p3?: MiBlock,
  ) => {
    const nameStyle = terminal ? TERMINAL_NAME_CELL : { ...NAME_CELL, backgroundColor: rowBg ?? NAME_CELL.backgroundColor };
    return (
      <>
        <TableRow key={`${rowKey}-target`} sx={{ backgroundColor: rowBg }}>
          <TableCell rowSpan={p3 ? 4 : 3} sx={nameStyle}>{name}</TableCell>
          <TableCell sx={TARGET_LABEL}>Target</TableCell>
          {targetCells(rowKey)}
          {targetTotalCell(rowKey)}
        </TableRow>
        <TableRow key={`${rowKey}-actual`} sx={{ backgroundColor: rowBg }}>
          <TableCell sx={LABEL_CELL}>Actual</TableCell>
          {periods.map(p => actualCell(cells?.[p.key], `${rowKey}-a-${p.key}`))}
          {actualTotalCell(rowKey, total)}
        </TableRow>
        <TableRow key={`${rowKey}-p12`} sx={{ backgroundColor: rowBg }}>
          <TableCell sx={P12_LABEL}>P1/P2</TableCell>
          {periods.map(p => p12Cell(cells?.[p.key], `${rowKey}-c-${p.key}`))}
          {p12TotalCell(rowKey, total)}
        </TableRow>
        {p3 && (
          <TableRow key={`${rowKey}-p3`} sx={{ backgroundColor: rowBg }}>
            <TableCell sx={LABEL_CELL}>P3 MI</TableCell>
            {periods.map(p => p12Cell(p3.periods?.[p.key], `${rowKey}-m-${p.key}`))}
            {p12TotalCell(rowKey, p3.total)}
          </TableRow>
        )}
      </>
    );
  };

  const regionLabel = (region: Region) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <IconButton
        size="small"
        aria-label={expanded.has(region.name) ? `Collapse ${region.name}` : `Expand ${region.name}`}
        aria-expanded={expanded.has(region.name)}
        onClick={() => toggleRegion(region.name)}
      >
        {expanded.has(region.name) ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
      </IconButton>
      <span>{region.name}</span>
      <Typography component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 0.5 }}>
        ({region.terminal_count})
      </Typography>
    </Box>
  );

  const renderRegion = (region: Region) => (
    <Box component="span" key={region.name} sx={{ display: 'contents' }}>
      {locationBlock(regionLabel(region), `region-${region.name}`, region.periods, region.total, '#f7f9fb', false, region.p3_mi)}
      {expanded.has(region.name) && region.terminals.map(t => (
        <Box component="span" key={`t-${region.name}-${t.name}`} sx={{ display: 'contents' }}>
          {locationBlock(t.name, `t-${region.name}-${t.name}`, t.periods, t.total, undefined, true, t.p3_mi)}
        </Box>
      ))}
    </Box>
  );

  // ── CSV export (the current filters, one row per metric per location) ──

  const exportCsv = () => {
    const header = ['Location Type', 'Region', 'Terminal', 'ISO Year', 'ISO Week', 'Period', 'Metric',
      'Target Availability', 'Actual Availability', 'Downtime seconds', 'Downtime hours', 'Distinct Incident Count', 'EAI Filter'];
    const lines: (string | number)[][] = [];
    const eaiLabel = eaiFilter === 'yes' ? 'Yes' : 'No';

    const addLocation = (locationType: string, region: string, terminal: string, key: string, label: string, cell: Cell | undefined, p3Count?: number) => {
      const iw = isoYearWeek(key);
      const downtime = cell?.downtime_seconds ?? 0;
      const base = [locationType, region, terminal, iw?.year ?? '', iw?.week ?? '', label];
      lines.push([...base, 'Target', target.toFixed(2), '', '', '', '', eaiLabel]);
      lines.push([...base, 'Actual', '', cell?.pct === null || cell?.pct === undefined ? '' : cell.pct.toFixed(2),
        Math.round(downtime), (downtime / 3600).toFixed(2), '', eaiLabel]);
      lines.push([...base, 'P1/P2', '', '', '', '', cell?.incident_count ?? 0, eaiLabel]);
      if (p3Count !== undefined) lines.push([...base, 'P3 MI', '', '', '', '', p3Count, eaiLabel]);
    };

    // Global P3 MI follows the same rule as the Global P1/P2 rows: only when no Region or Terminal is selected.
    if (showGlobalP12 && data.global_p3_mi) {
      periods.forEach(p => {
        const iw = isoYearWeek(p.key);
        lines.push(['Global', '', '', iw?.year ?? '', iw?.week ?? '', p.label, 'P3 MI', '', '', '', '', data.global_p3_mi?.periods[p.key]?.incident_count ?? 0, eaiLabel]);
      });
    }

    regionsToShow.forEach(region => {
      periods.forEach(p => addLocation('Region', region.name, '', p.key, p.label, region.periods[p.key],
        region.p3_mi ? (region.p3_mi.periods[p.key]?.incident_count ?? 0) : undefined));
      region.terminals.forEach(t => periods.forEach(p => addLocation('Terminal', region.name, t.name, p.key, p.label, t.periods[p.key],
        t.p3_mi ? (t.p3_mi.periods[p.key]?.incident_count ?? 0) : undefined)));
    });

    const csv = [header, ...lines].map(r => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `terminals-availability-${periodType}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a2b3c' }}>{source.title}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!data}>
            Export CSV
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => loadData()}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh data'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="avail-period-label">Period Type</InputLabel>
          <Select labelId="avail-period-label" label="Period Type" value={periodType}
            onChange={e => setPeriodType(e.target.value as typeof periodType)}>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="avail-range-label">Time Range</InputLabel>
          <Select labelId="avail-range-label" label="Time Range" value={timeRange}
            onChange={e => setTimeRange(e.target.value as typeof timeRange)}>
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="last7">Last 7 Days</MenuItem>
            <MenuItem value="last30">Last 30 Days</MenuItem>
            <MenuItem value="last90">Last 90 Days</MenuItem>
            <MenuItem value="ytd">Year to Date</MenuItem>
            {source.showPreviousYear && <MenuItem value="prev_year">Previous Year</MenuItem>}
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>

        {timeRange === 'custom' && (
          <>
            <TextField size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }}
              value={customStart} onChange={e => setCustomStart(e.target.value)} />
            <TextField size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }}
              value={customEnd} onChange={e => setCustomEnd(e.target.value)} error={customInvalid}
              helperText={customInvalid ? 'Enter both dates; start cannot be after end' : ''} />
          </>
        )}

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="avail-region-label">Region</InputLabel>
          <Select labelId="avail-region-label" label="Region" value={regionFilter}
            onChange={e => onRegionChange(e.target.value)}>
            <MenuItem value="all">All Regions</MenuItem>
            {data.regions.map(r => <MenuItem key={r.name} value={r.name}>{r.name}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="avail-terminal-label">Terminal</InputLabel>
          <Select labelId="avail-terminal-label" label="Terminal" value={terminalFilter}
            onChange={e => setTerminalFilter(e.target.value)}>
            <MenuItem value="all">All Terminals</MenuItem>
            {terminalOptions.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel id="avail-eai-label">EAI</InputLabel>
          <Select labelId="avail-eai-label" label="EAI" value={eaiFilter}
            onChange={e => setEaiFilter(e.target.value as typeof eaiFilter)}>
            <MenuItem value="no">No</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error} The values below are the last data received and may be out of date.
        </Alert>
      )}

      {data.unmapped_terminals.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Region mapping required for: {data.unmapped_terminals.join(', ')}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ overflowX: 'auto', maxWidth: '100%' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HDR_STYLE, textAlign: 'left', minWidth: 200, position: 'sticky', left: 0, zIndex: 3 }}>Location</TableCell>
              <TableCell sx={{ ...HDR_STYLE, textAlign: 'left', minWidth: 90 }}>Metric</TableCell>
              {periods.map(p => (
                <TableCell key={p.key} sx={{ ...HDR_STYLE, minWidth: 84 }}>{p.label}</TableCell>
              ))}
              <TableCell sx={{ ...HDR_STYLE, minWidth: 110 }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showGlobalP12 && (
              <>
                <TableRow sx={{ backgroundColor: '#e1f5fe' }}>
                  <TableCell rowSpan={data.global_p3_mi ? 3 : 2} sx={{ ...NAME_CELL, backgroundColor: '#e1f5fe' }}>Global</TableCell>
                  <TableCell sx={{ ...LABEL_CELL, color: '#6a1b9a' }}>P1/P2 Target</TableCell>
                  {globalTargetCells()}
                  <TableCell sx={TOTAL_CELL} />
                </TableRow>
                <TableRow sx={{ backgroundColor: '#e1f5fe' }}>
                  <TableCell sx={P12_LABEL}>P1/P2</TableCell>
                  {periods.map(p => globalP12Cell(data.global_p1p2.periods[p.key]?.incident_count ?? 0, `global-c-${p.key}`))}
                  <TableCell sx={{ ...TOTAL_CELL, color: 'text.primary' }}>{data.global_p1p2.total.incident_count}</TableCell>
                </TableRow>
                {data.global_p3_mi && (
                  <TableRow sx={{ backgroundColor: '#e1f5fe' }}>
                    <TableCell sx={LABEL_CELL}>P3 MI</TableCell>
                    {periods.map(p => p12Cell(data.global_p3_mi?.periods[p.key], `global-m-${p.key}`))}
                    {p12TotalCell('global', data.global_p3_mi.total)}
                  </TableRow>
                )}
              </>
            )}
            {regionsToShow.map(renderRegion)}
            <TableRow sx={{ backgroundColor: '#e1f5fe' }}>
              <TableCell sx={{ ...NAME_CELL, backgroundColor: '#e1f5fe', fontWeight: 700 }}>% Met Target</TableCell>
              <TableCell sx={LABEL_CELL} />
              {periods.map(p => {
                const m = data.met_target.periods[p.key];
                return (
                  <TableCell key={`met-${p.key}`} sx={{ ...VALUE_CELL, fontWeight: 700 }}>
                    {m ? metTargetText(m.met, m.total) : ''}
                  </TableCell>
                );
              })}
              <TableCell sx={{ ...TOTAL_CELL, fontWeight: 700 }}>
                {metTargetText(data.met_target.total.met, data.met_target.total.total)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography sx={{ mt: 1.5, fontSize: '0.75rem', color: 'text.secondary' }}>
        Availability = (168 h − downtime) ÷ 168 h per week, 24x7. Green = at or above {targetText}, red = below.
        Global P1/P2 target (whole Terminal estate): {incidentMax === null ? 'not defined' : `${incidentMax} per ${PERIOD_NOUN[periodType]}`}. Region and Terminal P1/P2 are informational.
        P3 MI = distinct priority 3 major incidents (accepted); counted only, no target.
      </Typography>
    </Box>
  );
}
